/**
 * EdgeModal.tsx — the relationship editor.
 *
 * The canvas shows ONE arrow per node pair (readable). The arrow's modal lists
 * every typed connection it carries — the spec's "I don't see how I can link
 * edges to certain params - it's just one edge... passing only weight". A
 * connection is one of:
 *   - rate   : a fixed per-tick flow from a source-node param (default 'rate')
 *   - derive : flow from another edge × a CEL expr (e.g. `x * params.price`)
 *   - signal : read-only state exposure (no flow); lets a target gate read the
 *              source node's state in its formula scope
 *
 * Each connection has its own CEL transform (ExprField), so a single edge can
 * carry a goods flow AND the resulting money, or expose a downstream buffer's
 * fill to an upstream gate. Backward-compat: a `derive` block on the edge
 * auto-migrates to one connection at compute time; this modal shows the
 * canonical `connections` array.
 *
 * #10 — custom flow colour via <input type="color">. The 5 fixed flowKinds
 * keep their semantic colours; only `custom` exposes the picker, stored on
 * `edge.customColor`.
 *
 * #11 — edge parameters are always editable (the "link param editor"). An edge
 * param is a tunable that lives on the edge (e.g. a `price` referenced by a
 * connection's `params.price`). Uses the same row shape as node params.
 */
import { useState } from 'react';
import { Plus, Trash, PencilSimple, TagSimple, X, Plugs, Link, LinkBreak } from '@phosphor-icons/react';

import type { EdgeConnection, FlowKind, GraphEdge, GraphNode, ParamSpec } from '../engine/types';
import { flowKindColor } from '../graph/palette';
import { Modal } from './Modal';
import { ExprField } from './ExprField';
import { AgentModal } from './AgentModal';
import { ParamRow } from './NodeModal';
import { BindingBadge, CellBindingPicker, type BindingTools } from './CellBindingPicker';
import { lookupBoundCell } from '../engine/bindings';
import type { LibraryAdapter } from '../library/LibraryAdapter';
import type { RemoteProjectAdapter } from '../projects/RemoteProjectAdapter';

interface EdgeModalProps {
  edge: GraphEdge;
  sourceNode: GraphNode;
  targetNode: GraphNode;
  /** All edges — for the derive-source picker + id-uniqueness check. */
  allEdges: GraphEdge[];
  /** Known group labels across the bundle — the autocomplete source for the
   *  [+ group] input (#3). */
  knownGroups?: string[];
  currentFlow?: number;
  /** True when the displayed flows come from an external model subprocess
   *  (api/relay mode) — the Current-flow readout says so, because editing
   *  the weight only affects the in-browser engine in that mode. */
  flowsFromModel?: boolean;
  /** Scope for the connection-expr live-eval: {x, params, source, target}. */
  scope: Record<string, unknown>;
  editable: boolean;
  onClose: () => void;
  onSetParam: (key: string, value: number) => void;
  onUpdate?: (edge: GraphEdge) => void;
  onDelete?: () => void;
  /** Rename the edge id (rewrites derive-connection sourceParam refs).
   *  Returns true if accepted (unique). */
  onRenameEdge?: (oldId: string, newId: string) => boolean;
  /** Set/clear the edge's group label (#3). */
  onSetGroup?: (edgeId: string, group: string | undefined) => void;
  /** Add a new group label to the bundle's known list (#3). */
  onAddGroup?: (group: string) => void;
  /** Re-anchor the edge's source/target handle to a different side of its
   *  node (#move-links). `which` is 'source' | 'target'; `handle` is one of
   *  the 8 named handles ('s-left' … 't-bottom') or undefined for default. */
  onSetHandle?: (edgeId: string, which: 'source' | 'target', handle: string | undefined) => void;
  /** Library adapter for the agent modal's file/linked-document picker. */
  libraryAdapter?: LibraryAdapter;
  /** Project backend for the agent modal's Code-tab source-file picker. */
  projectAdapter?: RemoteProjectAdapter;
  /** The project id whose `src/` to list in the Code-tab picker. */
  projectId?: string;
  tab?: 'settings' | 'params';
  onSwitchTab?: (tab: 'settings' | 'params') => void;
  /** DB access for cell bindings (edge.weightBinding + edge param bindings):
   *  the tables snapshot, its lazy load, and the PATCH seam for slider
   *  write-through. Absent in local mode. */
  bindingTools?: BindingTools;
}

const FLOW_KINDS: FlowKind[] = ['goods', 'money', 'orders', 'customers', 'signal', 'custom'];

export function EdgeModal({
  edge,
  sourceNode,
  targetNode,
  allEdges,
  knownGroups = [],
  currentFlow,
  flowsFromModel,
  scope,
  editable,
  onClose,
  onSetParam,
  onUpdate,
  onDelete,
  onRenameEdge,
  onSetGroup,
  onAddGroup,
  onSetHandle,
  libraryAdapter,
  projectAdapter,
  projectId,
  tab = 'settings',
  onSwitchTab,
  bindingTools,
}: EdgeModalProps): JSX.Element {
  const [draft, setDraft] = useState<GraphEdge>(() => structuredClone(edge));
  // #2 — id editable on double-click.
  const [editingId, setEditingId] = useState(false);
  const [idDraft, setIdDraft] = useState(edge.id);
  // Cell-binding picker for the fan-out weight (inline, under the weight row).
  const [weightPickerOpen, setWeightPickerOpen] = useState(false);
  // #3 — group add input toggle.
  const [addingGroup, setAddingGroup] = useState(false);
  const [groupDraft, setGroupDraft] = useState('');
  const [agentModalOpen, setAgentModalOpen] = useState(false);
  const commit = (next: GraphEdge) => {
    setDraft(next);
    onUpdate?.(next);
  };
  const color = flowKindColor(draft.flowKind, draft.customKind, draft.customColor);

  const setKind = (flowKind: FlowKind) =>
    commit({ ...draft, flowKind, customKind: flowKind === 'custom' ? (draft.customKind ?? 'custom') : undefined });
  const setCustomKind = (customKind: string) => commit({ ...draft, customKind });
  const setCustomColor = (customColor: string) => commit({ ...draft, customColor });

  // #2 — id rename with uniqueness check (against every other edge's id).
  const idTaken = allEdges.filter((e) => e.id !== edge.id).some((e) => e.id === idDraft);
  const commitId = () => {
    setEditingId(false);
    if (idDraft !== edge.id && !idTaken && onRenameEdge) {
      if (!onRenameEdge(edge.id, idDraft)) setIdDraft(edge.id);
    } else {
      setIdDraft(edge.id);
    }
  };

  // #3 — group add/assign. Updates local draft AND fires the bundle callbacks
  //  (so the groups panel + the running sim see the assignment immediately).
  const acceptGroup = (g: string) => {
    const trimmed = g.trim();
    if (trimmed) {
      commit({ ...draft, group: trimmed });
      onAddGroup?.(trimmed);
      onSetGroup?.(edge.id, trimmed);
    }
    setGroupDraft('');
    setAddingGroup(false);
  };

  // ─── Connections (#11 typed links) ──────────────────────────────────────
  const conns = draft.connections ?? [];
  const setConn = (idx: number, patch: Partial<EdgeConnection>) => {
    const next = conns.map((c, i) => (i === idx ? { ...c, ...patch } : c));
    commit({ ...draft, connections: next });
  };
  const addConn = () => {
    const c: EdgeConnection = { kind: 'rate', expr: 'x' };
    commit({ ...draft, connections: [...conns, c] });
  };
  const removeConn = (idx: number) =>
    commit({ ...draft, connections: conns.filter((_, i) => i !== idx) });

  // Source-node param options (for rate), source-edge options (for derive).
  const sourceParamOptions = sourceNode.params.map((p) => p.key);
  const deriveEdgeOptions = allEdges.filter((e) => e.id !== draft.id).map((e) => e.id);

  // ─── Edge params (#11 — the link param editor) ──────────────────────────
  const updateEdgeParam = (idx: number, patch: Partial<ParamSpec>) => {
    const next = draft.params.map((p, i) => (i === idx ? { ...p, ...patch } : p));
    commit({ ...draft, params: next });
  };
  const addEdgeParam = () => {
    const p: ParamSpec = {
      key: `param${Math.floor(Math.random() * 1e6).toString(36)}`,
      label: 'New param',
      value: 0,
      min: 0,
      max: 100,
      step: 1,
    };
    commit({ ...draft, params: [...draft.params, p] });
  };
  const removeEdgeParam = (idx: number) =>
    commit({ ...draft, params: draft.params.filter((_, i) => i !== idx) });
  const renameEdgeParam = (idx: number, newKey: string) => {
    const oldKey = draft.params[idx]?.key;
    if (!oldKey || oldKey === newKey) return;
    updateEdgeParam(idx, { key: newKey });
    // Edge params are referenced via `params.<key>` inside connection exprs;
    // the live-eval scope is rebuilt each render, so the rename flows through
    // without an explicit bundle rewrite (unlike node params).
  };

  return (
    <Modal
      title={`${sourceNode.label} → ${targetNode.label} (${tab === 'settings' ? 'Settings' : 'Params'})`}
      footer={
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          {onSwitchTab && (
            <button
              type="button"
              className="btn btn-mini"
              onClick={() => onSwitchTab(tab === 'settings' ? 'params' : 'settings')}
            >
              {tab === 'settings' ? 'Switch to Params' : 'Switch to Settings'}
            </button>
          )}
          {editable && (
            <button
              type="button"
              className="btn btn-mini btn-agent"
              title="API / Agent settings"
              onClick={() => setAgentModalOpen(true)}
            >
              <Plugs size={14} weight="fill" />
              {draft.agentConfig ? 'Edit agent' : 'Add agent'}
              {draft.agentConfig && <span className="agent-dot" title="Agent configured" />}
            </button>
          )}
        </div>
      }
      onClose={onClose}
    >
      {tab === 'settings' && (
        <>
          {/* ─── Endpoints: which side of the source/target node this edge anchors
              to. Drag-reconnect is the primary path; these dropdowns are precise. */}
          {editable && (
            <section className="modal-section endpoint-row">
          <label className="modal-field endpoint-field">
            <span className="field-label">From {sourceNode.label} ●</span>
            <select
              className="seg-select"
              value={draft.sourceHandle ?? ''}
              onChange={(e) => {
                const v = e.target.value || undefined;
                commit({ ...draft, sourceHandle: v });
                onSetHandle?.(edge.id, 'source', v);
              }}
            >
              <option value="">auto</option>
              <option value="s-left">left</option>
              <option value="s-left-t">left (upper)</option>
              <option value="s-left-b">left (lower)</option>
              <option value="s-top">top</option>
              <option value="s-top-l">top (left)</option>
              <option value="s-top-r">top (right)</option>
              <option value="s-right">right</option>
              <option value="s-right-t">right (upper)</option>
              <option value="s-right-b">right (lower)</option>
              <option value="s-bottom">bottom</option>
              <option value="s-bottom-l">bottom (left)</option>
              <option value="s-bottom-r">bottom (right)</option>
            </select>
          </label>
          <label className="modal-field endpoint-field">
            <span className="field-label">To {targetNode.label} ●</span>
            <select
              className="seg-select"
              value={draft.targetHandle ?? ''}
              onChange={(e) => {
                const v = e.target.value || undefined;
                commit({ ...draft, targetHandle: v });
                onSetHandle?.(edge.id, 'target', v);
              }}
            >
              <option value="">auto</option>
              <option value="t-left">left</option>
              <option value="t-left-t">left (upper)</option>
              <option value="t-left-b">left (lower)</option>
              <option value="t-top">top</option>
              <option value="t-top-l">top (left)</option>
              <option value="t-top-r">top (right)</option>
              <option value="t-right">right</option>
              <option value="t-right-t">right (upper)</option>
              <option value="t-right-b">right (lower)</option>
              <option value="t-bottom">bottom</option>
              <option value="t-bottom-l">bottom (left)</option>
              <option value="t-bottom-r">bottom (right)</option>
            </select>
          </label>
        </section>
      )}
      {/* ─── Flow kind + colour ───────────────────────────────────────────
          The 5 fixed kinds (goods/money/orders/customers/signal) get their
          semantic colour automatically. Only `custom` exposes a name input +
          a colour swatch as an input-extension button (no "Colour" label — it's
          obvious from the swatch). */}
      <section className="modal-section">
        {editable && (
          <div className="kind-row">
            {FLOW_KINDS.map((k) => (
              <button
                key={k}
                type="button"
                className={`kind-chip${draft.flowKind === k ? ' sel' : ''}`}
                style={{ borderColor: flowKindColor(k, draft.customKind, draft.customColor) }}
                onClick={() => setKind(k)}
              >{k}</button>
            ))}
          </div>
        )}
        {draft.flowKind === 'custom' && (
          <div className="custom-kind-row">
            {editable ? (
              <div className="input-with-ext">
                <input
                  type="text"
                  className="text-input"
                  value={draft.customKind ?? ''}
                  onChange={(e) => setCustomKind(e.target.value)}
                  placeholder="custom kind name (e.g. ingredients)"
                />
                {/* Color picker as input-extension button — no label, the swatch
                    IS the affordance. Native color input hidden behind a chip. */}
                <label className="color-ext" title="Pick a colour">
                  <span className="color-ext-swatch" style={{ background: color }} />
                  <input
                    type="color"
                    className="color-input-hidden"
                    value={/^#[0-9a-fA-F]{6}$/.test(draft.customColor ?? '') ? draft.customColor : color}
                    onChange={(e) => setCustomColor(e.target.value)}
                  />
                </label>
              </div>
            ) : (
              <div className="modal-kv">
                <span className="flow-swatch" style={{ background: color }} />
                <code>{draft.customKind ?? 'custom'}</code>
              </div>
            )}
          </div>
        )}
        {/* Fan-out weight — how weight-routed edges get its value: the
            flow is the SOURCE node's outflow × this weight, where sibling
            weights (the source's other out-edges) are NORMALISED. The
            readout shows the actual share so the normalisation is visible:
            a 0.8 weight among siblings summing to 1.6 routes 50%, not 80%.
            The weight is also a param (e:<id>.weight) and may be BOUND to a
            table cell (weightBinding) — the DB then drives it, and a bound
            value may exceed 1 (the split normalises: binding the five
            platform edges to raw budget $ gives a budget-proportional
            split). */}
        <div className="modal-kv">
          <span
            title={`Flow = ${sourceNode.label}'s outflow × weight ÷ Σ sibling weights. Siblings: the source's other out-edges.`}
          >
            Fan-out weight
          </span>
          {editable ? (
            draft.weightBinding ? (
              <span className="binding-inline-value">
                <code>{lookupBoundCell(bindingTools?.tables, draft.weightBinding).value ?? `⚠ ${draft.weight}`}</code>
                <em className="param-fallback">DB-driven · fallback {draft.weight}</em>
              </span>
            ) : (
              <input
                type="number"
                className="param-num-input"
                min={0}
                max={1}
                step={0.1}
                value={draft.weight}
                title="Authored fallback weight ∈ [0,1] — the live value (param e:<id>.weight) applies when nothing overrides it"
                onChange={(e) => {
                  const v = Number(e.target.value);
                  commit({ ...draft, weight: v });
                  // Live effect through the param system (implicit 'weight'
                  // key) — the in-browser engine reads it next tick.
                  onSetParam('weight', v);
                }}
              />
            )
          ) : (
            <code>
              {draft.weightBinding
                ? (lookupBoundCell(bindingTools?.tables, draft.weightBinding).value ?? `⚠ ${draft.weight}`)
                : draft.weight}
            </code>
          )}
          {editable && (
            <button
              type="button"
              className={`input-ext-btn${draft.weightBinding ? ' on' : ''}`}
              title={draft.weightBinding
                ? 'Edit table-cell binding'
                : 'Bind to a table cell (the DB drives the split — e.g. budget rows)'}
              onClick={() => setWeightPickerOpen((o) => !o)}
            >
              {draft.weightBinding ? <Link size={14} /> : <LinkBreak size={14} className="opacity-icon" />}
            </button>
          )}
        </div>
        {editable && draft.weightBinding && (
          <BindingBadge
            binding={draft.weightBinding}
            tools={bindingTools}
            onOpen={() => setWeightPickerOpen(true)}
            onUnbind={() => commit({ ...draft, weightBinding: null })}
          />
        )}
        {editable && weightPickerOpen && (
          <CellBindingPicker
            value={draft.weightBinding ?? null}
            onChange={(b) => commit({ ...draft, weightBinding: b })}
            tools={bindingTools}
            onClose={() => setWeightPickerOpen(false)}
          />
        )}
        <div className="modal-kv">
          <span title="The weight actually applied: this edge's resolved weight ÷ the sum of ALL of the source node's out-edge resolved weights (cell-bound weights count at their DB value).">Effective share</span>
          <code>
            {(() => {
              const resolved = (e: GraphEdge): number => {
                if (bindingTools && e.weightBinding) {
                  return lookupBoundCell(bindingTools.tables, e.weightBinding).value ?? e.weight;
                }
                return e.weight;
              };
              const sibSum = allEdges
                .filter((e) => e.source === edge.source && e.flowKind !== 'signal')
                .reduce((acc, e) => acc + (resolved(e) || 0), 0);
              const share = sibSum > 0 ? resolved(draft) / sibSum : 0;
              return `${Math.round(share * 100)}% of ${sourceNode.label} (Σw ${sibSum.toFixed(2)})`;
            })()}
          </code>
        </div>
        <div className="modal-kv">
          <span>Conserves flow</span>
          {editable ? (
            <input
              type="checkbox"
              checked={draft.conserves !== false}
              onChange={(e) => commit({ ...draft, conserves: e.target.checked ? undefined : false })}
              title="Off = display-only flow: animated fan-out, but the target's inflow/stock never receive it"
            />
          ) : (
            <code>{draft.conserves === false ? 'no (display-only)' : 'yes'}</code>
          )}
        </div>
        <div className="modal-kv">
          <span>Current flow</span>
          <code>{currentFlow !== undefined ? fmt(currentFlow) : '—'}</code>
        </div>
        {/* Provenance — WHERE the flow number comes from. Weight + share
            above describe the ROUTING RULE; this says who applies it: the
            project's model subprocess (api/relay — graph edits don't move
            the live number) or the in-browser engine (local edit mode). */}
        <div className="modal-hint">
          {flowsFromModel
            ? 'Flow posted by the running model — the weight steers only the in-browser engine; change the model\u2019s knobs (its params tables) to move this number.'
            : (draft.connections ?? []).length > 0
              ? 'Flow derived by the in-browser engine from the typed connection\u2019s CEL expr (Params tab).'
              : 'Flow computed by the in-browser engine: source node\u2019s outflow × weight ÷ Σ sibling weights.'}
        </div>
        {/* #2 — Edge ID: read-only <code>, double-click to edit (same pattern as
            NodeModal's id row). Validates uniqueness across the bundle. */}
        <div className="edge-id-row">
          {editable && editingId ? (
            <>
              <input
                type="text"
                className={`text-input edge-id-input${idTaken ? ' invalid' : ''}`}
                value={idDraft}
                onChange={(e) => setIdDraft(e.target.value)}
                onBlur={commitId}
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') commitId();
                  if (e.key === 'Escape') { setIdDraft(edge.id); setEditingId(false); }
                }}
              />
              {idTaken && <span className="id-hint">already in use</span>}
            </>
          ) : (
            <button
              type="button"
              className="edge-id-display"
              title={editable ? 'Double-click to rename' : 'Edge ID'}
              onDoubleClick={() => editable && (setEditingId(true), setIdDraft(edge.id))}
            >
              <code>{draft.id}</code>
              {editable && <PencilSimple size={12} />}
            </button>
          )}
        </div>
        {/* #3 — Group assignment + [+ group] add button. */}
        {editable && (
          <div className="edge-group-row">
            {draft.group && (
              <span className="group-chip">
                {draft.group}
                <button
                  type="button"
                  className="group-chip-x"
                  title="Remove from group"
                  onClick={() => {
                    commit({ ...draft, group: undefined });
                    onSetGroup?.(edge.id, undefined);
                  }}
                ><X size={11} weight="bold" /></button>
              </span>
            )}
            {addingGroup ? (
              <div className="group-add-row">
                <input
                  type="text"
                  className="text-input group-add-input"
                  list="known-groups-list"
                  placeholder="group name"
                  value={groupDraft}
                  onChange={(e) => setGroupDraft(e.target.value)}
                  onBlur={() => { if (groupDraft.trim()) acceptGroup(groupDraft); else setAddingGroup(false); }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') acceptGroup(groupDraft);
                    if (e.key === 'Escape') { setGroupDraft(''); setAddingGroup(false); }
                  }}
                  autoFocus
                />
                {/* Autocomplete source = every group ever seen on any edge + bundle.groups. */}
                <datalist id="known-groups-list">
                  {knownGroups.map((g) => <option key={g} value={g} />)}
                </datalist>
              </div>
            ) : (
              <button type="button" className="btn btn-mini btn-add" onClick={() => setAddingGroup(true)}>
                <TagSimple size={12} /> {draft.group ? 'change group' : '+ group'}
              </button>
            )}
          </div>
        )}
      </section>

      {editable && onDelete && (
        <section className="modal-section">
          <button type="button" className="btn btn-danger" onClick={onDelete}>🗑 Delete edge</button>
        </section>
      )}
      </>
      )}
      {tab === 'params' && (
        <>
      {/* ─── Connections ─────────────────────────────────────────────────── */}
      <section className="modal-section">
        <div className="modal-subhead">Typed connections ({conns.length})</div>
        <p className="param-hint">
          Each connection is one typed link along this edge. <code>rate</code> = fixed per-tick from
          a source param; <code>derive</code> = flow from another edge × a CEL expr; <code>signal</code> =
          read-only state exposure (no flow).
        </p>
        {conns.map((c, i) => (
          <div key={i} className="conn-row">
            <div className="conn-head">
              <select
                className="seg-select"
                value={c.kind}
                disabled={!editable}
                onChange={(e) => setConn(i, { kind: e.target.value as EdgeConnection['kind'] })}
              >
                <option value="rate">rate</option>
                <option value="derive">derive</option>
                <option value="signal">signal</option>
              </select>
              {editable && (
                <button type="button" className="input-ext-btn" title="Remove connection" onClick={() => removeConn(i)}>
                  <Trash size={14} />
                </button>
              )}
            </div>
            {/* Source picker */}
            <label className="modal-field">
              <span className="field-label">{c.kind === 'derive' ? 'source edge' : c.kind === 'signal' ? 'source state' : 'source param'}</span>
              {c.kind === 'derive' ? (
                <select
                  className="seg-select"
                  value={c.sourceParam ?? ''}
                  disabled={!editable}
                  onChange={(e) => setConn(i, { sourceParam: e.target.value })}
                >
                  <option value="">— pick an edge —</option>
                  {deriveEdgeOptions.map((id) => <option key={id} value={id}>{id}</option>)}
                </select>
              ) : c.kind === 'signal' ? (
                <select
                  className="seg-select"
                  value={c.sourceParam ?? 'stock'}
                  disabled={!editable}
                  onChange={(e) => setConn(i, { sourceParam: e.target.value })}
                >
                  {['stock', 'inflow', 'outflow', ...(targetNode.params.map((p) => p.key))].map((k) => (
                    <option key={k} value={k}>{k}</option>
                  ))}
                </select>
              ) : (
                <select
                  className="seg-select"
                  value={c.sourceParam ?? 'rate'}
                  disabled={!editable}
                  onChange={(e) => setConn(i, { sourceParam: e.target.value })}
                >
                  {(sourceParamOptions.length > 0 ? sourceParamOptions : ['rate']).map((k) => (
                    <option key={k} value={k}>{k}</option>
                  ))}
                </select>
              )}
            </label>
            {/* Target field (resource kinds only) */}
            {c.kind !== 'signal' && (
              <label className="modal-field">
                <span className="field-label">target field</span>
                <input
                  type="text"
                  className="text-input"
                  value={c.targetField ?? 'inflow'}
                  disabled={!editable}
                  onChange={(e) => setConn(i, { targetField: e.target.value })}
                  placeholder="inflow (default) or a state key"
                />
              </label>
            )}
            {/* CEL transform */}
            {editable ? (
              <ExprField
                label="Transform (CEL)"
                value={c.expr ?? 'x'}
                scope={scope}
                onChange={(expr) => setConn(i, { expr })}
                placeholder=" e.g. x * params.price  — `x` is the source value, `params` is this edge's params"
              />
            ) : (
              <div className="modal-kv"><span>Transform</span><code>{c.expr ?? 'x'}</code></div>
            )}
          </div>
        ))}
        {editable && (
          <button type="button" className="btn btn-mini btn-add" onClick={addConn}>
            <Plus size={14} weight="bold" /> connection
          </button>
        )}
      </section>

      {/* ─── Edge parameters (#11 — the link param editor) ────────────────── */}
      <section className="modal-section">
        <div className="modal-subhead">Edge parameters ({draft.params.length})</div>
        <p className="param-hint">
          Tunables that live on this edge. Referenced inside connection transforms via{' '}
          <code>params.&lt;key&gt;</code> (e.g. <code>params.price</code>).
        </p>
        {draft.params.map((p, i) => (
          <ParamRow
            key={p.key + i}
            spec={p}
            editable={editable}
            duplicate={draft.params.filter((x) => x.key === p.key).length > 1}
            hideLabel
            onChange={(patch) => updateEdgeParam(i, patch)}
            onRename={(nk) => renameEdgeParam(i, nk)}
            onRemove={() => removeEdgeParam(i)}
            onLiveChange={(v) => onSetParam(p.key, v)}
            bindingTools={bindingTools}
          />
        ))}
        {editable && (
          <button type="button" className="btn btn-mini btn-add" onClick={addEdgeParam}>
            <Plus size={14} weight="bold" /> edge param
          </button>
        )}
      </section>

      {/* ─── API / Agent Settings (mirrors NodeModal's footer button) ────
          Opens the same AgentModal, but the host is this edge: input params
          come from the edge's connections (rate/derive/signal sources), and
          the output knobs are the edge's own params (price, latency, …).
          The button lives in the Modal footer; see `footer` below. */}
      {!editable && draft.agentConfig && (
        <section className="modal-section">
          <div className="modal-subhead">Agent</div>
          <div className="modal-kv-grid">
            {draft.agentConfig.name && <KV label="Name" value={draft.agentConfig.name} />}
            <KV label="Mode" value={draft.agentConfig.mode ?? 'api'} />
            <KV label="Target" value={draft.agentConfig.target || '—'} />
            {draft.agentConfig.linkedDocumentId && <KV label="Linked doc" value={draft.agentConfig.linkedDocumentId} />}
          </div>
        </section>
      )}

      {agentModalOpen && (
        <AgentModal
          host={{
            // The title carries '→' so AgentModal can detect edge vs node
            // context for its copy + param labeling.
            title: `${sourceNode.label} → ${targetNode.label}`,
            paramOptions: draft.params.map((p) => ({ key: p.key, label: p.label || p.key, path: `e:${draft.id}.${p.key}` })),
            inputDescription:
              conns.length > 0
                ? `The agent receives this edge's ${conns.length} connection(s) (${conns.map((c) => c.kind).join(', ')}) as input.`
                : 'The agent receives this edge\'s flow + param values as input.',
          }}
          config={draft.agentConfig}
          editable={editable}
          libraryAdapter={libraryAdapter}
          projectAdapter={projectAdapter}
          projectId={projectId}
          onClose={() => setAgentModalOpen(false)}
          onChange={(cfg) => commit({ ...draft, agentConfig: cfg })}
        />
      )}
      </>
      )}
    </Modal>
  );
}

function fmt(n: number): string {
  if (Math.abs(n) >= 10000) return (n / 1000).toFixed(1) + 'k';
  if (Number.isInteger(n)) return String(n);
  return n.toFixed(2);
}

function KV({ label, value }: { label: string; value: string }): JSX.Element {
  return <div className="modal-kv"><span>{label}</span><code>{value}</code></div>;
}
